/**
 * The command center: one page that answers "where am I, and what should I do next".
 *
 * It is driven by `GET /api/development/overview` — the primary track, the current stage, path
 * progress, skill counts, active work, certification prep, weak areas and the resume checklist —
 * plus the workplace, learning, portfolio and program feeds. Every number traces to a record the
 * server holds; nothing is invented, and nothing here is a qualification.
 */
import { catalog, deliverables, interview, learn, program, progress, request, sessions, workplace } from '../../api/client';
import { development } from '../../api/development';
import { skills } from '../../api/skills';
import { coach } from '../../api/coach';
import { curriculum as curriculumApi } from '../../api/curriculum';
import { guideHref } from '../../lib/curriculum';
import type { IconName } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';
import { useStore } from '../../state/store';
import { href } from '../../router';
import { ErrorNotice, humanize } from '../../components/common';
import { Avatar, EmptyState, Icon, useCountUp } from '../../components/ui';
import { streakCopy } from '../../lib/development';
import type { AchievementFacts } from '../../lib/achievements';
import { ActivityFeed } from '../workplace/ActivityFeed';
import {
  ActiveWorkCard,
  CertificationCard,
  CoachCard,
  FoundationsCard,
  LearningProgressCard,
  MilestonesCard,
  MoreRecommendations,
  NextBestAction,
  PortfolioCard,
  ProgramCard,
  ReviewsCard,
  SkillMasteryCard,
  TracksCard,
  WeakAreasCard,
  WorkplaceCard,
} from './cards';
import '../../styles/pages/workplace.css';
import '../../styles/pages/develop.css';
import '../../styles/pages/curriculum.css';

interface ProjectCatalogEntry {
  id: string;
  title: string;
}

export function DashboardPage() {
  const { user, profile } = useStore();
  const careerId = profile?.selectedCareerId;

  const overview = useAsync(async () => (careerId ? (await development.overview()).overview : undefined), [careerId]);
  const recs = useAsync(async () => (careerId ? (await development.recommendations()).recommendations : []), [careerId]);
  const wp = useAsync(async () => (await workplace.overview()).workplace, [careerId]);
  const notifications = useAsync(async () => (await workplace.notifications()).notifications, []);
  const scenarios = useAsync(async () => (await catalog.scenarios()).scenarios, []);
  const labs = useAsync(async () => (await learn.labs()).labs, []);
  const projectCatalog = useAsync(async () => (await request<{ projects: ProjectCatalogEntry[] }>('GET', '/projects')).projects, []);
  const certifications = useAsync(async () => (await learn.certifications()).certifications, []);
  const careers = useAsync(async () => (await catalog.careers()).careers, []);
  const programRun = useAsync(async () => (await program.current()).program ?? undefined, []);
  const learnProgress = useAsync(() => learn.progress(), []);
  const dash = useAsync(async () => (await progress.dashboard()).progress, [careerId]);
  const sessionList = useAsync(async () => (await sessions.list()).sessions, []);
  const portfolio = useAsync(async () => (await deliverables.list()).deliverables, []);
  const interviews = useAsync(async () => (await interview.sessions()).sessions, []);
  const foundations = useAsync(async () => (await skills.foundations()).skills, [user?.id]);
  const coachSuggestions = useAsync(() => coach.suggestions(), [user?.id]);
  const careerCurriculum = useAsync(async () => (careerId ? (await curriculumApi.career(careerId)).curriculum : undefined), [careerId, user?.id]);

  if (!careerId) return <ChooseTrack name={user?.displayName ?? 'there'} />;

  const o = overview.data;
  const scenarioTitle = (id: string) => scenarios.data?.find((s) => s.id === id)?.title ?? humanize(id);
  const labTitle = (id: string) => labs.data?.find((l) => l.id === id)?.title ?? humanize(id);
  const projectTitle = (id: string) => projectCatalog.data?.find((p) => p.id === id)?.title ?? humanize(id);
  const certName = (id: string) => certifications.data?.find((c) => String(c.id) === id)?.name ?? humanize(id);
  const careerLabel = (id: string) => careers.data?.find((c) => c.id === id)?.name ?? humanize(id);

  const cur = careerCurriculum.data;
  const nextSkill = cur?.nextSkill
    ? { careerName: cur.careerName, skillId: cur.nextSkill.skillId, name: cur.nextSkill.name, to: cur.skillMap.some((t) => t.skills.some((sk) => sk.skillId === cur.nextSkill!.skillId && sk.guide)) ? guideHref(cur.nextSkill.skillId) : href({ page: 'skill', id: cur.nextSkill.skillId }) }
    : undefined;

  const ranked = recs.data ?? [];
  const next = o?.nextAction ?? ranked[0] ?? null;
  const others = ranked.filter((r) => !(next && r.kind === next.kind && r.id === next.id)).slice(0, 3);

  const labsPassed = (learnProgress.data?.labs ?? []).filter((l) => l.passed);
  const lessonsPassed = (learnProgress.data?.lessons ?? []).filter((l) => l.passed);
  const facts: AchievementFacts = {
    completedScenarios: (dash.data?.completedScenarios ?? []).map((s) => ({
      scenarioId: String(s.scenarioId),
      careerId: String(s.careerId),
      score: s.score,
      completedAt: s.completedAt,
      trainingMode: (sessionList.data ?? []).find((x) => x.scenarioId === String(s.scenarioId) && x.status === 'completed')?.trainingMode,
    })),
    deliverables: (portfolio.data ?? []).map((d) => ({ deliverableType: d.deliverableType, createdAt: d.createdAt })),
    lessonsPassed: lessonsPassed.map((l) => ({ lessonId: String(l.lessonId), at: l.at })),
    labsPassed: labsPassed.map((l) => ({ labId: l.labId, mode: l.mode, completedAt: l.completedAt })),
    interviews: interviews.data ?? [],
    tiers: (dash.data?.tiers ?? []).map((t) => ({ careerId: String(t.careerId), tier: String(t.tier) })),
  };

  return (
    <div className="dashboard page-enter">
      <Hero
        name={user?.displayName ?? 'there'}
        careerName={o ? o.primaryCareer.name : careerLabel(careerId)}
        careerId={careerId}
        stageTitle={o?.stage?.title}
        streak={o?.streak}
        scenariosDone={o?.progress.scenarios.done ?? 0}
        labsDone={o?.progress.labs.done ?? 0}
        portfolioCount={o?.portfolioCount ?? 0}
      />

      <ErrorNotice message={overview.error} onRetry={overview.reload} />

      <NextBestAction rec={next} career={careerId} loading={overview.loading && recs.loading} error={recs.error} onRetry={recs.reload} />
      <MoreRecommendations recs={others} career={careerId} loading={recs.loading} />

      <div className="dash-grid">
        <CoachCard data={coachSuggestions.data} loading={coachSuggestions.loading} error={coachSuggestions.error} onRetry={coachSuggestions.reload} onChanged={coachSuggestions.reload} index={0} nextSkill={nextSkill} />
        <ActiveWorkCard overview={o} scenarioTitle={scenarioTitle} labTitle={labTitle} projectTitle={projectTitle} loading={overview.loading} index={0} />
        <LearningProgressCard overview={o} careerId={careerId} loading={overview.loading} index={1} />
        <SkillMasteryCard overview={o} loading={overview.loading} index={2} />
        <FoundationsCard steps={foundations.data} loading={foundations.loading} error={foundations.error} onRetry={foundations.reload} index={3} />
        <CertificationCard prep={o?.certificationPrep} dueReviews={o?.dueReviews} certName={certName} loading={overview.loading} index={3} />
        <PortfolioCard count={o?.portfolioCount} checklist={o?.resumeChecklist} loading={overview.loading} index={4} />
        <WeakAreasCard areas={o?.weakAreas} career={careerId} loading={overview.loading} index={5} />
        <MilestonesCard facts={facts} loading={dash.loading || learnProgress.loading} index={6} />
        <WorkplaceCard
          assignments={wp.data?.assignments}
          notifications={notifications.data}
          managerName={wp.data?.manager?.name}
          loading={wp.loading && !wp.data}
          error={wp.error}
          onRetry={wp.reload}
          onChanged={() => { notifications.reload(); wp.reload(); }}
          index={7}
        >
          <ActivityFeed limit={10} heading="Live workplace activity" headingId="dash-activity" level={3} showEmptyAction={false} />
        </WorkplaceCard>
      </div>

      <details className="more-section">
        <summary><Icon name="menu" size={16} aria-hidden="true" /> More about your development</summary>
        <div className="dash-grid">
          <ReviewsCard dueReviews={o?.dueReviews} prep={o?.certificationPrep} certName={certName} loading={overview.loading} index={0} />
          <ProgramCard program={programRun.data} loading={programRun.loading && !programRun.data} index={1} />
          <TracksCard overview={o} careerLabel={careerLabel} loading={overview.loading} index={2} />
        </div>
      </details>

      {o && <p className="notice small">{o.notice}</p>}
    </div>
  );
}

/* ---------- hero ---------- */

function Hero({ name, careerName, careerId, stageTitle, streak, scenariosDone, labsDone, portfolioCount }: { name: string; careerName: string; careerId: string; stageTitle?: string; streak?: { current: number; longest: number; lastDay?: string }; scenariosDone: number; labsDone: number; portfolioCount: number }) {
  const today = new Date();
  const scenarios = useCountUp(scenariosDone);
  const labs = useCountUp(labsDone);
  const items = useCountUp(portfolioCount);
  const copy = streakCopy(streak ?? { current: 0, longest: 0 });
  return (
    <header className="hero fade-in">
      <div>
        <span className="stamp"><Icon name="calendar" size={14} aria-hidden="true" /> {today.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        <h1>Welcome back, {name}.</h1>
        <p className="lede">Your career goal: {careerName}.</p>
        <p className="goal">
          {stageTitle ? <>Current stage: <strong>{stageTitle}</strong>. </> : null}
          <a href={href({ page: 'progress', tab: 'stages' })}>See your stages</a> · <a href={href({ page: 'career', id: careerId })}>About this track</a>
        </p>
        <span className="streak" title={copy.detail}>
          <Icon name="flag" size={14} aria-hidden="true" /> {copy.headline}
        </span>
        <p className="goal">{copy.detail}</p>
      </div>
      <div className="hero-stats">
        <div className="tile"><span className="value tabular">{scenarios}</span><span className="label">Scenarios on your path done</span></div>
        <div className="tile"><span className="value tabular">{labs}</span><span className="label">Labs on your path done</span></div>
        <div className="tile"><span className="value tabular">{items}</span><span className="label">Portfolio items</span></div>
      </div>
    </header>
  );
}

/* ---------- first visit: no track chosen yet ---------- */

function ChooseTrack({ name }: { name: string }) {
  return (
    <div className="dashboard page-enter">
      <section className="choose-hero fade-in" aria-labelledby="choose-title">
        <span className="eyebrow" style={{ color: 'var(--cyan)' }}>Welcome to TechCore Solutions</span>
        <h1 id="choose-title">Welcome, {name}. Choose your track.</h1>
        <p>
          Every scenario, colleague and assignment here belongs to a career track. Pick one and you get a seat in a department, a manager, a 30-day program and real workplace tasks to practice. You can change track later without losing anything you have done.
        </p>
        <div className="actions">
          <a className="button large on-navy" href={href({ page: 'onboarding' })}>Start onboarding</a>
          <a className="button large on-navy secondary" href={href({ page: 'explore' })}>Browse career tracks</a>
        </div>
      </section>
      <div className="grid" style={{ marginTop: 'var(--sp-5)' }}>
        {[
          { icon: 'compass' as IconName, title: 'Explore the tracks', body: 'Simulated tracks across support, infrastructure, cloud, data, security and customer success.', to: href({ page: 'explore' }) },
          { icon: 'map' as IconName, title: 'See how they connect', body: 'The career map shows which tracks share skills and where people move next.', to: href({ page: 'career-map' }) },
          { icon: 'book' as IconName, title: 'Start learning now', body: 'Lessons and hands-on labs are open to everyone, whichever track you end up choosing.', to: href({ page: 'learn' }) },
        ].map((c, i) => (
          <a className="card hoverable reveal" style={{ ['--i' as string]: i }} key={c.title} href={c.to}>
            <span className="icon-chip" aria-hidden="true"><Icon name={c.icon} size={18} /></span>
            <h2 style={{ fontSize: 'var(--fs-lg)', marginTop: 'var(--sp-3)' }}>{c.title}</h2>
            <p className="small muted">{c.body}</p>
          </a>
        ))}
      </div>
      <EmptyState icon="user" title="Nothing is recorded against your account yet" actions={<a className="button small" href={href({ page: 'onboarding' })}>Choose a track</a>}>
        <Avatar name={name} size="sm" /> Once you pick a track, this page becomes your command center: what to do next, what is in flight and what your evidence says.
      </EmptyState>
    </div>
  );
}
